/**
 * ChessGame - Integrates CustomChessEngine (rules/validation) with ChessAI
 * Handles all chess logic for ChessRogue battles with support for arbitrary board configurations
 */

import { CustomChessEngine, Piece, BoardConfig } from './CustomChessEngine';
import { ChessAI } from './ChessAI';
import { PieceType, PieceColor, Square, ChessMove, AIConfig, Obstacle, PieceUpgrade, UpgradeContext, PostMoveEffect } from '../../types';

export class ChessGame {
  private engine: CustomChessEngine;
  private aiConfig: AIConfig;
  private boardConfig: BoardConfig;
  private playerBackRank: number = 1;
  private obstacles: Set<string> = new Set();
  private waterSquares: Set<string> = new Set();
  private walls: Map<string, Set<string>> = new Map();
  private playerUpgradeValues: Set<string> = new Set();
  private turnCount: number = 0;

  // Per-battle ability tracking
  private usedAbilities: Set<string> = new Set();
  private stunnedSquares: Set<string> = new Set();

  // Teleporter system
  private teleporterPairs: Map<string, string> = new Map();
  private teleporterUses: Map<string, number> = new Map();
  private teleporterMaxUses: number = 1;

  // Fog of war system
  private fogEnabled: boolean = false;
  private visibleSquares: Set<string> = new Set();
  private fogVisibilityRadius: number = 1;

  // Chess clock integration
  private frozenEnemyClockTurns: number = 0;

  constructor(
    fen?: string,
    aiConfig?: AIConfig,
    restrictedArea?: { minCol: number; maxCol: number; minRank: number; maxRank: number }
  ) {
    this.boardConfig = this.createBoardConfig(restrictedArea);
    this.engine = new CustomChessEngine(this.boardConfig);

    if (fen) {
      this.loadFEN(fen);
    } else {
      this.setupStandardPosition();
    }

    this.aiConfig = aiConfig || { depth: 4, difficulty: 'medium' };
  }

  private createBoardConfig(restrictedArea?: {
    minCol: number;
    maxCol: number;
    minRank: number;
    maxRank: number;
  }): BoardConfig {
    console.log('=== CREATE BOARD CONFIG ===');
    console.log('Restricted area:', restrictedArea);

    const validSquares = new Set<string>();

    if (restrictedArea) {
      for (let col = restrictedArea.minCol; col <= restrictedArea.maxCol; col++) {
        for (let rank = restrictedArea.minRank; rank <= restrictedArea.maxRank; rank++) {
          const file = String.fromCharCode(97 + col);
          const square = `${file}${rank}`;
          validSquares.add(square);
        }
      }
      console.log('Valid squares for restricted board:', Array.from(validSquares).sort());
      this.playerBackRank = restrictedArea.minRank;

      return {
        validSquares,
        width: 8,
        height: 8,
        obstacles: this.obstacles,
      };
    } else {
      for (let col = 0; col < 8; col++) {
        for (let rank = 1; rank <= 8; rank++) {
          const file = String.fromCharCode(97 + col);
          const square = `${file}${rank}`;
          validSquares.add(square);
        }
      }

      return {
        validSquares,
        width: 8,
        height: 8,
        obstacles: this.obstacles,
      };
    }
  }

  loadFEN(fen: string): boolean {
    console.log('=== LOAD FEN ===');
    console.log('FEN string:', fen);

    try {
      const parts = fen.split(' ');
      const position = parts[0];
      const turn = (parts[1] === 'b' ? 'b' : 'w') as PieceColor;

      console.log('Position:', position);
      console.log('Turn:', turn);

      const pieces: Piece[] = [];
      const ranks = position.split('/');
      const skippedPieces: Array<{square: string, reason: string}> = [];

      for (let rankIndex = 0; rankIndex < ranks.length; rankIndex++) {
        const rank = 8 - rankIndex;
        const rankStr = ranks[rankIndex];
        let file = 0;

        for (const char of rankStr) {
          if (char >= '1' && char <= '9') {
            file += parseInt(char);
          } else {
            const color: PieceColor = char === char.toUpperCase() ? 'w' : 'b';
            const type = char.toLowerCase() as PieceType;
            const square = `${String.fromCharCode(97 + file)}${rank}` as Square;

            if (this.boardConfig.validSquares.has(square)) {
              pieces.push({ type, color, square });
            } else {
              skippedPieces.push({ square, reason: 'not in valid squares' });
            }

            file++;
          }
        }
      }

      console.log('Parsed pieces:', pieces.map(p => `${p.color}${p.type}@${p.square}`).join(', '));
      if (skippedPieces.length > 0) {
        console.log('Skipped pieces (outside valid area):', skippedPieces);
      }

      this.engine.setupPosition(pieces, turn);
      console.log('Engine setup complete with turn:', turn);
      return true;
    } catch (error) {
      console.error('Failed to load FEN:', error);
      return false;
    }
  }

  private setupStandardPosition(): void {
    const fen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    this.loadFEN(fen);
  }

  /**
   * Build and apply UpgradeContext to the engine
   */
  private buildAndApplyUpgradeContext(): void {
    if (this.playerUpgradeValues.size === 0) {
      this.engine.setUpgradeContext(undefined);
      return;
    }

    const ctx: UpgradeContext = {
      values: this.playerUpgradeValues,
      used: this.usedAbilities,
      pieceColor: 'w',
      stunnedSquares: this.stunnedSquares,
    };
    this.engine.setUpgradeContext(ctx);

    // Enable multi-king if player has add_king upgrade
    if (this.playerUpgradeValues.has('add_king')) {
      this.engine.setMultiKingMode(true);
    }
  }

  makeMove(from: Square, to: Square, promotion?: PieceType): ChessMove | null {
    const piece = this.getPieceAt(from);
    const pieceType = piece?.type;
    const pieceColor = piece?.color;
    if (!this.isSquareAccessible(to, pieceType, pieceColor)) {
      return { from, to, promotion, isLegal: false };
    }

    if (pieceType && this.isBlockedByWall(from, to, pieceType, pieceColor)) {
      return { from, to, promotion, isLegal: false };
    }

    // Capture immunity: enemy pieces can't capture immune player pieces
    if (pieceColor === 'b' && pieceType) {
      const target = this.getPieceAt(to);
      if (target && target.color === 'w') {
        if (pieceType === 'p' && this.playerUpgradeValues.has('pawn_immunity')) {
          return { from, to, promotion, isLegal: false };
        }
        // center_pawn_immunity: pieces on center 4 squares can't be captured by pawns
        if (pieceType === 'p' && this.playerUpgradeValues.has('center_pawn_immunity')) {
          const centerSquares = this.getCenterSquares();
          if (centerSquares.includes(to)) {
            return { from, to, promotion, isLegal: false };
          }
        }
        if ((pieceType === 'n' || pieceType === 'b') && this.playerUpgradeValues.has('minor_immunity')) {
          return { from, to, promotion, isLegal: false };
        }
        if (this.playerUpgradeValues.has('king_aura') && this.isAdjacentToPlayerKing(to)) {
          return { from, to, promotion, isLegal: false };
        }
        // knight_outpost: knights protected by a friendly pawn can't be captured
        if (target.type === 'n' && this.playerUpgradeValues.has('knight_outpost') && this.isProtectedByPawn(to)) {
          return { from, to, promotion, isLegal: false };
        }
        // formation_shield: pieces adjacent to friendly piece survive first capture per battle
        if (this.playerUpgradeValues.has('formation_shield')
            && !this.usedAbilities.has(`formation:${to}`)
            && this.isAdjacentToFriendlyPiece(to)) {
          this.usedAbilities.add(`formation:${to}`);
          return { from, to, promotion, isLegal: false };
        }
        // tower_defense: rooks on back rank can't be captured
        if (target.type === 'r' && this.playerUpgradeValues.has('tower_defense')) {
          const toRank = parseInt(to[1]);
          if (toRank === this.playerBackRank) {
            return { from, to, promotion, isLegal: false };
          }
        }
        // bloodlust: piece that just captured can't be captured until next turn
        if (this.playerUpgradeValues.has('bloodlust') && this.usedAbilities.has(`bloodlust:${to}`)) {
          return { from, to, promotion, isLegal: false };
        }
        // sacred_ground: pieces on the sacred square can't be captured
        if (this.playerUpgradeValues.has('sacred_ground') && this.usedAbilities.has(`sacred:${to}`)) {
          return { from, to, promotion, isLegal: false };
        }
      }
    }

    // Apply upgrade context before making moves
    this.buildAndApplyUpgradeContext();

    const result = this.engine.makeMove(from, to, promotion);

    if (!result.isLegal) {
      return { from, to, promotion, isLegal: false };
    }

    return {
      from,
      to,
      promotion,
      captured: result.captured,
      isLegal: true,
    };
  }

  /**
   * Get post-move effect after a move has been executed
   */
  getPostMoveEffect(_from: Square, to: Square, captured: PieceType | undefined, pieceType: PieceType, pieceColor: PieceColor): PostMoveEffect {
    // Only player pieces get post-move effects
    if (pieceColor !== 'w') {
      // Enemy captured player piece: check stun_capturer
      if (captured && pieceColor === 'b' && this.playerUpgradeValues.has('stun_capturer')) {
        this.stunnedSquares.add(to);
        return { type: 'stun', piece: to, message: 'Enemy piece stunned!' };
      }
      return { type: 'none' };
    }

    // extra_move_first_capture: first knight capture each turn -> extra move
    if (captured && pieceType === 'n' && this.playerUpgradeValues.has('extra_move_first_capture')
        && !this.usedAbilities.has('extra_move_first_capture_turn')) {
      this.usedAbilities.add('extra_move_first_capture_turn');
      return { type: 'extra_move', piece: to, message: 'Chain Leap! Knight moves again.' };
    }

    return { type: 'none' };
  }

  /**
   * Mark a once-per-battle ability as used
   */
  /**
   * Find a square marked with the given prefix (e.g. 'sacred', 'bounty').
   * Returns the square portion of a `prefix:square` ability marker, or null.
   */
  getMarkedSquare(prefix: string): string | null {
    for (const key of this.usedAbilities) {
      if (key.startsWith(prefix + ':')) {
        return key.slice(prefix.length + 1);
      }
    }
    return null;
  }

  markAbilityUsed(key: string): void {
    this.usedAbilities.add(key);
  }

  /**
   * Check if an ability has been used
   */
  isAbilityUsed(key: string): boolean {
    return this.usedAbilities.has(key);
  }

  /**
   * Reset per-battle state (called at battle start)
   */
  resetBattleState(): void {
    this.usedAbilities.clear();
    this.stunnedSquares.clear();
    this.turnCount = 0;
    this.frozenEnemyClockTurns = 0;
    this.teleporterUses.clear();
  }

  /**
   * The four central squares of the CURRENT board (board size varies by floor),
   * used by center-control style upgrades.
   */
  getCenterSquares(): string[] {
    let minCol = 7, maxCol = 0, minRank = 8, maxRank = 1;
    for (const sq of this.boardConfig.validSquares) {
      const col = sq.charCodeAt(0) - 97;
      const rank = parseInt(sq[1]);
      if (col < minCol) minCol = col;
      if (col > maxCol) maxCol = col;
      if (rank < minRank) minRank = rank;
      if (rank > maxRank) maxRank = rank;
    }
    const midColLow = Math.floor((minCol + maxCol) / 2);
    const midRankLow = Math.floor((minRank + maxRank) / 2);
    const squares: string[] = [];
    for (const c of [midColLow, midColLow + 1]) {
      for (const r of [midRankLow, midRankLow + 1]) {
        if (c >= minCol && c <= maxCol && r >= minRank && r <= maxRank) {
          squares.push(String.fromCharCode(97 + c) + r);
        }
      }
    }
    return squares;
  }

  /**
   * Clear stunned squares at start of a new turn
   */
  clearStuns(): void {
    this.stunnedSquares.clear();
    // Reset per-turn ability flags
    this.usedAbilities.delete('extra_move_first_capture_turn');
    // Clear bloodlust immunity (expires each turn)
    for (const key of Array.from(this.usedAbilities)) {
      if (key.startsWith('bloodlust:')) {
        this.usedAbilities.delete(key);
      }
    }
    this.buildAndApplyUpgradeContext();
  }

  /**
   * Check if movement from one square to another is blocked by a wall
   */
  private isBlockedByWall(from: Square, to: Square, pieceType: PieceType, pieceColor?: PieceColor): boolean {
    if (pieceType === 'n') return false;
    if (pieceColor === 'w' && this.playerUpgradeValues.has('wall_immunity')) return false;

    const fromFile = from.charCodeAt(0) - 97;
    const fromRank = parseInt(from[1]);
    const toFile = to.charCodeAt(0) - 97;
    const toRank = parseInt(to[1]);

    const fileDiff = toFile - fromFile;
    const rankDiff = toRank - fromRank;

    const isOrthogonal = (fileDiff === 0 || rankDiff === 0);
    const isDiagonal = Math.abs(fileDiff) === Math.abs(rankDiff);

    if (isOrthogonal) {
      return this.checkOrthogonalPath(fromFile, fromRank, toFile, toRank);
    } else if (isDiagonal) {
      return this.checkDiagonalPath(fromFile, fromRank, toFile, toRank);
    }

    return false;
  }

  private checkOrthogonalPath(fromFile: number, fromRank: number, toFile: number, toRank: number): boolean {
    let direction: string;
    let stepFile = 0;
    let stepRank = 0;

    if (toRank > fromRank) { direction = 'north'; stepRank = 1; }
    else if (toRank < fromRank) { direction = 'south'; stepRank = -1; }
    else if (toFile > fromFile) { direction = 'east'; stepFile = 1; }
    else { direction = 'west'; stepFile = -1; }

    const oppositeDirection = this.getOppositeDirection(direction);

    let currentFile = fromFile;
    let currentRank = fromRank;

    while (currentFile !== toFile || currentRank !== toRank) {
      const currentSquare = String.fromCharCode(97 + currentFile) + currentRank;
      const currentWalls = this.walls.get(currentSquare);
      if (currentWalls && currentWalls.has(direction)) return true;

      currentFile += stepFile;
      currentRank += stepRank;

      const nextSquare = String.fromCharCode(97 + currentFile) + currentRank;
      const nextWalls = this.walls.get(nextSquare);
      if (nextWalls && nextWalls.has(oppositeDirection)) return true;
    }

    return false;
  }

  private checkDiagonalPath(fromFile: number, fromRank: number, toFile: number, toRank: number): boolean {
    const fileStep = toFile > fromFile ? 1 : -1;
    const rankStep = toRank > fromRank ? 1 : -1;

    let currentFile = fromFile;
    let currentRank = fromRank;

    while (currentFile !== toFile && currentRank !== toRank) {
      const nextFile = currentFile + fileStep;
      const nextRank = currentRank + rankStep;

      const adjacentSquare1 = String.fromCharCode(97 + currentFile) + nextRank;
      const adjacentSquare2 = String.fromCharCode(97 + nextFile) + currentRank;

      const hasWall1 = this.hasWallObstacle(adjacentSquare1);
      const hasWall2 = this.hasWallObstacle(adjacentSquare2);

      if (hasWall1 && hasWall2) return true;

      currentFile += fileStep;
      currentRank += rankStep;
    }

    return false;
  }

  private hasWallObstacle(square: string): boolean {
    return this.obstacles.has(square);
  }

  private getOppositeDirection(direction: string): string {
    switch (direction) {
      case 'north': return 'south';
      case 'south': return 'north';
      case 'east': return 'west';
      case 'west': return 'east';
      default: return '';
    }
  }

  private isSquareAccessible(square: Square, _pieceType?: PieceType, pieceColor?: PieceColor): boolean {
    if (this.obstacles.has(square)) return false;

    if (this.waterSquares.has(square)) {
      if (pieceColor === 'w') {
        if (this.playerUpgradeValues.has('water_immunity')) return true;
        if (this.playerUpgradeValues.has('water_immune_3_turns') && this.turnCount < 3) return true;
      }
      return false;
    }

    return true;
  }

  async getAIMove(): Promise<ChessMove | null> {
    try {
      const ai = new ChessAI(this.aiConfig);
      const bestMove = ai.getBestMove(
        this.engine,
        this.obstacles,
        this.waterSquares,
        this.walls,
        this.boardConfig.validSquares
      );

      if (!bestMove) {
        console.warn('ChessAI found no move, falling back to random');
        return this.getRandomMove();
      }

      return this.makeMove(bestMove.from, bestMove.to, bestMove.promotion);
    } catch (error) {
      console.error('AI move error:', error);
      return this.getRandomMove();
    }
  }

  /**
   * Get AI's intended move without executing it (for show_ai_move upgrade)
   */
  async peekAIMove(): Promise<ChessMove | null> {
    try {
      const ai = new ChessAI(this.aiConfig);
      const bestMove = ai.getBestMove(
        this.engine,
        this.obstacles,
        this.waterSquares,
        this.walls,
        this.boardConfig.validSquares
      );
      if (!bestMove) return null;
      return { from: bestMove.from, to: bestMove.to, promotion: bestMove.promotion, isLegal: true };
    } catch {
      return null;
    }
  }

  private getRandomMove(isInCheck: boolean = false): ChessMove | null {
    const allPieces = this.engine.getAllPieces().filter(p => p.color === this.getTurn());

    const allMoves: { from: Square; to: Square }[] = [];
    for (const piece of allPieces) {
      const moves = this.getLegalMoves(piece.square);
      for (const to of moves) {
        allMoves.push({ from: piece.square, to });
      }
    }

    if (allMoves.length === 0) {
      if (isInCheck) {
        console.warn('AI is in check but has no legal moves to escape');
      }
      return null;
    }

    if (isInCheck) {
      console.log(`AI is in check, found ${allMoves.length} legal moves to escape`);
    }

    const randomMove = allMoves[Math.floor(Math.random() * allMoves.length)];

    const piece = this.getPieceAt(randomMove.from);
    let promotion: PieceType | undefined;
    if (piece && piece.type === 'p') {
      const toRank = parseInt(randomMove.to[1]);
      // Promote at the board's actual last rank for each color (restricted
      // boards promote at e.g. rank 7 / rank 2, not 8 / 1).
      let minRank = 8, maxRank = 1;
      for (const sq of this.boardConfig.validSquares) {
        const r = parseInt(sq[1]);
        if (r < minRank) minRank = r;
        if (r > maxRank) maxRank = r;
      }
      if ((piece.color === 'b' && toRank === minRank) || (piece.color === 'w' && toRank === maxRank)) {
        promotion = 'q';
      }
    }

    return this.makeMove(randomMove.from, randomMove.to, promotion);
  }

  getLegalMoves(square: Square): Square[] {
    console.log('=== GET LEGAL MOVES (ChessGame) ===');
    console.log('Square:', square);

    const piece = this.getPieceAt(square);
    console.log('Piece at square:', piece);

    if (!piece) {
      console.log('No piece at square, returning []');
      return [];
    }

    // Build and apply upgrade context before getting moves
    this.buildAndApplyUpgradeContext();

    const legalMoves = this.engine.getLegalMoves(square);
    console.log('Engine returned', legalMoves.length, 'moves:', legalMoves);

    const filtered = legalMoves.filter(to => {
      if (!this.isSquareAccessible(to, piece.type, piece.color)) {
        console.log(`  - Filtered out ${to}: not accessible (water/obstacle)`);
        return false;
      }

      if (this.isBlockedByWall(square, to, piece.type, piece.color)) {
        console.log(`  - Filtered out ${to}: blocked by wall`);
        return false;
      }

      // For enemy (black) pieces: check capture immunity upgrades
      if (piece.color === 'b') {
        const target = this.getPieceAt(to);
        if (target && target.color === 'w') {
          if (piece.type === 'p' && this.playerUpgradeValues.has('pawn_immunity')) {
            console.log(`  - Filtered out ${to}: pawn_immunity blocks pawn capture`);
            return false;
          }
          if (piece.type === 'p' && this.playerUpgradeValues.has('center_pawn_immunity')) {
            const centerSquares = this.getCenterSquares();
            if (centerSquares.includes(to)) {
              console.log(`  - Filtered out ${to}: center_pawn_immunity blocks pawn capture on center`);
              return false;
            }
          }
          if ((piece.type === 'n' || piece.type === 'b') && this.playerUpgradeValues.has('minor_immunity')) {
            console.log(`  - Filtered out ${to}: minor_immunity blocks minor piece capture`);
            return false;
          }
          if (this.playerUpgradeValues.has('king_aura') && this.isAdjacentToPlayerKing(to)) {
            console.log(`  - Filtered out ${to}: king_aura protects adjacent piece`);
            return false;
          }
          if (target.type === 'n' && this.playerUpgradeValues.has('knight_outpost') && this.isProtectedByPawn(to)) {
            console.log(`  - Filtered out ${to}: knight_outpost protects knight`);
            return false;
          }
          if (this.playerUpgradeValues.has('formation_shield')
              && !this.usedAbilities.has(`formation:${to}`)
              && this.isAdjacentToFriendlyPiece(to)) {
            console.log(`  - Filtered out ${to}: formation_shield protects piece`);
            this.usedAbilities.add(`formation:${to}`);
            return false;
          }
          if (target.type === 'r' && this.playerUpgradeValues.has('tower_defense')) {
            const toRank = parseInt(to[1]);
            if (toRank === this.playerBackRank) {
              console.log(`  - Filtered out ${to}: tower_defense protects rook on back rank`);
              return false;
            }
          }
          if (this.playerUpgradeValues.has('bloodlust') && this.usedAbilities.has(`bloodlust:${to}`)) {
            console.log(`  - Filtered out ${to}: bloodlust protects capturing piece`);
            return false;
          }
          if (this.playerUpgradeValues.has('sacred_ground') && this.usedAbilities.has(`sacred:${to}`)) {
            console.log(`  - Filtered out ${to}: sacred_ground protects piece`);
            return false;
          }
        }
      }

      return true;
    });

    // Add teleporter destinations
    if (this.teleporterPairs.size > 0 && piece.color === 'w') {
      const teleporterMoves = this.getTeleporterMoves(filtered);
      for (const tm of teleporterMoves) {
        if (!filtered.includes(tm)) {
          filtered.push(tm);
        }
      }
    }

    console.log('After filtering:', filtered.length, 'moves remain:', filtered);
    return filtered;
  }

  /**
   * Get additional moves from teleporter destinations
   */
  private getTeleporterMoves(currentMoves: Square[]): Square[] {
    const extraMoves: Square[] = [];
    for (const move of currentMoves) {
      // Check if destination is a teleporter entry
      const paired = this.teleporterPairs.get(move);
      if (paired) {
        const usesKey = `${move}-${paired}`;
        const currentUses = this.teleporterUses.get(usesKey) || 0;
        const maxUses = this.playerUpgradeValues.has('teleport_unlimited') ? Infinity : this.teleporterMaxUses;

        if (currentUses < maxUses) {
          const exitPiece = this.engine.getPieceAt(paired as Square);
          if (!exitPiece) {
            extraMoves.push(paired as Square);
          }
        }
      }

      // Check bidirectional
      if (this.playerUpgradeValues.has('teleport_bidirectional')) {
        for (const [entry, exit] of this.teleporterPairs.entries()) {
          if (exit === move) {
            const usesKey = `${entry}-${exit}`;
            const currentUses = this.teleporterUses.get(usesKey) || 0;
            const maxUses = this.playerUpgradeValues.has('teleport_unlimited') ? Infinity : this.teleporterMaxUses;
            if (currentUses < maxUses) {
              const entryPiece = this.engine.getPieceAt(entry as Square);
              if (!entryPiece && !extraMoves.includes(entry as Square)) {
                extraMoves.push(entry as Square);
              }
            }
          }
        }
      }
    }
    return extraMoves;
  }

  getPieceAt(square: Square): { type: PieceType; color: PieceColor } | null {
    return this.engine.getPieceAt(square);
  }

  isCheck(): boolean {
    return this.engine.isCheck();
  }

  isCheckmate(): boolean {
    // Apply context before checking
    this.buildAndApplyUpgradeContext();
    return this.engine.isCheckmate();
  }

  isStalemate(): boolean {
    this.buildAndApplyUpgradeContext();
    return this.engine.isStalemate();
  }

  isDraw(): boolean {
    return false;
  }

  isGameOver(): boolean {
    return this.isCheckmate() || this.isStalemate();
  }

  getTurn(): PieceColor {
    return this.engine.getTurn();
  }

  switchTurn(): void {
    this.engine.switchTurn();
    this.turnCount++;
  }

  getBoard(): (({ type: PieceType; color: PieceColor } | null)[][]) {
    return this.engine.getBoard() as any;
  }

  getFEN(): string {
    const board = this.engine.getBoard();
    let fen = '';

    for (let rank = 7; rank >= 0; rank--) {
      let emptyCount = 0;

      for (let file = 0; file < 8; file++) {
        const piece = board[7 - rank][file];

        if (!piece) {
          emptyCount++;
        } else {
          if (emptyCount > 0) {
            fen += emptyCount.toString();
            emptyCount = 0;
          }
          const pieceChar = piece.type;
          fen += piece.color === 'w' ? pieceChar.toUpperCase() : pieceChar;
        }
      }

      if (emptyCount > 0) {
        fen += emptyCount.toString();
      }

      if (rank > 0) {
        fen += '/';
      }
    }

    const turn = this.getTurn();
    fen += ` ${turn} - - 0 1`;

    return fen;
  }

  setObstacles(obstacles: Obstacle[]): void {
    this.obstacles.clear();
    this.waterSquares.clear();
    this.walls.clear();
    this.teleporterPairs.clear();

    for (const obstacle of obstacles) {
      switch (obstacle.type) {
        case 'wall':
          if (obstacle.blocksDirection) {
            if (!this.walls.has(obstacle.square)) {
              this.walls.set(obstacle.square, new Set());
            }
            this.walls.get(obstacle.square)!.add(obstacle.blocksDirection);
          } else {
            this.obstacles.add(obstacle.square);
          }
          break;
        case 'void':
          this.obstacles.add(obstacle.square);
          break;
        case 'pit':
          this.obstacles.add(obstacle.square);
          break;
        case 'water':
          this.waterSquares.add(obstacle.square);
          break;
        case 'teleport':
          if (obstacle.pairedWith) {
            this.teleporterPairs.set(obstacle.square, obstacle.pairedWith);
          }
          break;
      }
    }

    this.boardConfig.obstacles = this.obstacles;
  }

  setAIDifficulty(config: AIConfig): void {
    this.aiConfig = config;
  }

  getEngine(): CustomChessEngine {
    return this.engine;
  }

  getObstacleData(): { obstacles: Set<string>; waterSquares: Set<string>; walls: Map<string, Set<string>> } {
    return {
      obstacles: this.obstacles,
      waterSquares: this.waterSquares,
      walls: this.walls,
    };
  }

  getValidSquares(): Set<string> {
    return this.boardConfig.validSquares;
  }

  setActiveUpgrades(upgrades: PieceUpgrade[]): void {
    this.playerUpgradeValues.clear();
    for (const u of upgrades) {
      if (typeof u.effect.value === 'string') {
        this.playerUpgradeValues.add(u.effect.value);
      }
    }

    // Configure teleporter uses
    if (this.playerUpgradeValues.has('teleport_uses_1')) {
      this.teleporterMaxUses = 2;
    }
    if (this.playerUpgradeValues.has('teleport_unlimited')) {
      this.teleporterMaxUses = Infinity;
    }

    // Configure fog visibility
    if (this.playerUpgradeValues.has('fog_reveal_1')) {
      this.fogVisibilityRadius = 2;
    }
    if (this.playerUpgradeValues.has('fog_range_2')) {
      this.fogVisibilityRadius = 2;
    }
    if (this.playerUpgradeValues.has('fog_immunity')) {
      this.fogVisibilityRadius = Infinity;
    }

    // Configure frozen enemy clock
    if (this.playerUpgradeValues.has('freeze_enemy_clock_5')) {
      this.frozenEnemyClockTurns = 5;
    }

    // Build and apply context
    this.buildAndApplyUpgradeContext();
  }

  /** Check if player has a specific upgrade value */
  hasUpgrade(value: string): boolean {
    return this.playerUpgradeValues.has(value);
  }

  /** Get all active upgrade values */
  getUpgradeValues(): Set<string> {
    return this.playerUpgradeValues;
  }

  incrementTurnCount(): void {
    this.turnCount++;
    // Decrement frozen clock turns
    if (this.frozenEnemyClockTurns > 0) {
      this.frozenEnemyClockTurns--;
    }
  }

  getTurnCount(): number {
    return this.turnCount;
  }

  /** Check if enemy clock should be frozen this turn */
  isEnemyClockFrozen(): boolean {
    return this.frozenEnemyClockTurns > 0;
  }

  // === Environmental Manipulation ===

  /** Add a water square (for place_water_2 upgrade) */
  addWaterSquare(square: string): void {
    this.waterSquares.add(square);
  }

  /** Remove a water square */
  removeWaterSquare(square: string): void {
    this.waterSquares.delete(square);
  }

  /** Add a wall (for place_walls_2 upgrade) */
  addWall(square: string, direction: string): void {
    if (!this.walls.has(square)) {
      this.walls.set(square, new Set());
    }
    this.walls.get(square)!.add(direction);
  }

  /** Remove a wall */
  removeWall(square: string, direction: string): void {
    const wallDirs = this.walls.get(square);
    if (wallDirs) {
      wallDirs.delete(direction);
      if (wallDirs.size === 0) {
        this.walls.delete(square);
      }
    }
  }

  /** Remove a full-square obstacle (wall without direction) */
  removeObstacle(square: string): void {
    this.obstacles.delete(square);
    this.boardConfig.obstacles = this.obstacles;
  }

  /** Add a teleporter pair */
  addTeleporterPair(entry: string, exit: string): void {
    this.teleporterPairs.set(entry, exit);
    this.teleporterUses.set(`${entry}-${exit}`, 0);
  }

  /** Get teleporter pairs for rendering */
  getTeleporterPairs(): Map<string, string> {
    return this.teleporterPairs;
  }

  // === Fog of War ===

  /** Enable fog of war */
  setFogEnabled(enabled: boolean): void {
    this.fogEnabled = enabled;
    if (enabled) {
      this.updateFogVisibility();
    }
  }

  isFogEnabled(): boolean {
    return this.fogEnabled;
  }

  /** Update visible squares based on player piece positions */
  updateFogVisibility(): void {
    if (!this.fogEnabled || this.playerUpgradeValues.has('fog_immunity')) {
      // All squares visible
      this.visibleSquares = new Set(this.boardConfig.validSquares);
      return;
    }

    this.visibleSquares.clear();
    const playerPieces = this.engine.getAllPieces().filter(p => p.color === 'w');

    for (const piece of playerPieces) {
      const file = piece.square.charCodeAt(0) - 97;
      const rank = parseInt(piece.square[1]) - 1;

      for (let df = -this.fogVisibilityRadius; df <= this.fogVisibilityRadius; df++) {
        for (let dr = -this.fogVisibilityRadius; dr <= this.fogVisibilityRadius; dr++) {
          const f = file + df;
          const r = rank + dr;
          if (f >= 0 && f < 8 && r >= 0 && r < 8) {
            const sq = String.fromCharCode(97 + f) + (r + 1);
            if (this.boardConfig.validSquares.has(sq)) {
              this.visibleSquares.add(sq);
            }
          }
        }
      }
    }
  }

  /** Get visible squares for fog rendering */
  getVisibleSquares(): Set<string> {
    return this.visibleSquares;
  }

  /** Get water squares for rendering */
  getWaterSquares(): Set<string> {
    return this.waterSquares;
  }

  /** Get wall data for rendering */
  getWalls(): Map<string, Set<string>> {
    return this.walls;
  }

  private isAdjacentToPlayerKing(square: string): boolean {
    const allPieces = this.engine.getAllPieces();
    const playerKings = allPieces.filter(p => p.type === 'k' && p.color === 'w');
    if (playerKings.length === 0) return false;

    for (const king of playerKings) {
      const kingFile = king.square.charCodeAt(0);
      const kingRank = parseInt(king.square[1]);
      const sqFile = square.charCodeAt(0);
      const sqRank = parseInt(square[1]);

      if (Math.abs(kingFile - sqFile) <= 1 && Math.abs(kingRank - sqRank) <= 1
        && !(kingFile === sqFile && kingRank === sqRank)) {
        return true;
      }
    }
    return false;
  }

  /** Remove a piece from the board (for sacrifice effects) */
  removePieceAt(square: Square): void {
    this.engine.removePieceAt(square);
  }

  /** Check if a square has a player knight protected by a friendly pawn */
  private isProtectedByPawn(square: string): boolean {
    const sqFile = square.charCodeAt(0) - 97;
    const sqRank = parseInt(square[1]);
    // A white pawn protects diagonally forward: check if there's a white pawn
    // at (file-1, rank-1) or (file+1, rank-1) — pawns protect the square ahead-diagonal
    for (const df of [-1, 1]) {
      const pawnFile = sqFile + df;
      const pawnRank = sqRank - 1; // White pawns protect from below
      if (pawnFile >= 0 && pawnFile < 8 && pawnRank >= 1 && pawnRank <= 8) {
        const pawnSquare = (String.fromCharCode(97 + pawnFile) + pawnRank) as Square;
        const piece = this.engine.getPieceAt(pawnSquare);
        if (piece && piece.type === 'p' && piece.color === 'w') {
          return true;
        }
      }
    }
    return false;
  }

  /** Check if a square has a friendly piece adjacent to another friendly piece */
  private isAdjacentToFriendlyPiece(square: string): boolean {
    const sqFile = square.charCodeAt(0) - 97;
    const sqRank = parseInt(square[1]);
    const offsets = [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]];
    for (const [df, dr] of offsets) {
      const f = sqFile + df;
      const r = sqRank + dr;
      if (f >= 0 && f < 8 && r >= 1 && r <= 8) {
        const adjSquare = (String.fromCharCode(97 + f) + r) as Square;
        if (adjSquare === square) continue;
        const piece = this.engine.getPieceAt(adjSquare);
        if (piece && piece.color === 'w') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get random empty squares on enemy half of board (for place_water_2, place_walls_2)
   */
  getRandomEnemyHalfSquares(count: number): string[] {
    const validSquares = Array.from(this.boardConfig.validSquares);
    const ranks = validSquares.map(sq => parseInt(sq[1]));
    const minRank = Math.min(...ranks);
    const maxRank = Math.max(...ranks);
    const midRank = Math.floor((minRank + maxRank) / 2);

    // Enemy half = upper ranks
    const enemySquares = validSquares.filter(sq => {
      const rank = parseInt(sq[1]);
      return rank > midRank && !this.obstacles.has(sq) && !this.waterSquares.has(sq) && !this.engine.getPieceAt(sq as Square);
    });

    const result: string[] = [];
    const used = new Set<string>();
    for (let i = 0; i < count && enemySquares.length > 0; i++) {
      const idx = Math.floor(Math.random() * enemySquares.length);
      const sq = enemySquares[idx];
      if (!used.has(sq)) {
        result.push(sq);
        used.add(sq);
      }
      enemySquares.splice(idx, 1);
    }
    return result;
  }

  /**
   * Get random empty squares on player half of board
   */
  getRandomPlayerHalfSquares(count: number): string[] {
    const validSquares = Array.from(this.boardConfig.validSquares);
    const ranks = validSquares.map(sq => parseInt(sq[1]));
    const minRank = Math.min(...ranks);
    const maxRank = Math.max(...ranks);
    const midRank = Math.floor((minRank + maxRank) / 2);

    const playerSquares = validSquares.filter(sq => {
      const rank = parseInt(sq[1]);
      return rank <= midRank && !this.obstacles.has(sq) && !this.waterSquares.has(sq) && !this.engine.getPieceAt(sq as Square);
    });

    const result: string[] = [];
    const used = new Set<string>();
    for (let i = 0; i < count && playerSquares.length > 0; i++) {
      const idx = Math.floor(Math.random() * playerSquares.length);
      const sq = playerSquares[idx];
      if (!used.has(sq)) {
        result.push(sq);
        used.add(sq);
      }
      playerSquares.splice(idx, 1);
    }
    return result;
  }

  destroy(): void {
    // No external resources to clean up
  }
}
